<template>
  <div>
    <div>
      <v-subheader>
        User Engagement
      </v-subheader>

      <canvas
        ref="userEngagementChart"
      />
    </div>

    <div
      class="mt-6"
    >
      <v-subheader>
        Stickiness (DAU / MAU)
      </v-subheader>

      <canvas
        ref="stickinessChart"
      />
    </div>

    <div
      class="mt-6"
    >
      <v-subheader>
        Active User Retention (existing + new)
      </v-subheader>

      <canvas
        ref="activeUserRetentionChart"
      />
    </div>

    <div
      class="mt-6"
    >
      <v-subheader>
        Existing User Retention
      </v-subheader>

      <canvas
        ref="existingUserRetentionChart"
      />
    </div>

    <div
      class="mt-6"
    >
      <v-subheader>
        New User Retention
      </v-subheader>

      <canvas
        ref="newUserRetentionChart"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapWaitingActions } from 'vue-wait';
import Chart from 'chart.js';

export default {
  computed: {
    ...mapGetters({
      userEngagement: 'admin/stats/userEngagement',
      activeUserRetention: 'admin/stats/activeUserRetention',
      existingUserRetention: 'admin/stats/existingUserRetention',
      newUserRetention: 'admin/stats/newUserRetention',
    }),

    retention1dOptions() {
      return {
        label: '1 day',
        backgroundColor: [
          'rgba(255, 99, 132, 0.2)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      };
    },

    retention7dOptions() {
      return {
        label: '1 week',
        backgroundColor: [
          'rgba(54, 162, 235, 0.2)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
        ],
        borderWidth: 1,
      };
    },

    retention30dOptions() {
      return {
        label: '1 month',
        backgroundColor: [
          'rgba(255, 159, 64, 0.2)',
        ],
        borderColor: [
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      };
    },
  },

  async mounted() {
    await Promise.all([
      this.loadUserEngagement(),
      this.loadActiveUserRetention(),
      this.loadExistingUserRetention(),
      this.loadNewUserRetention(),
    ]);

    this.$nextTick(() => {
      this.initUserEngagementChart();
      this.initStickinessChart();
      this.initActiveUserRetentionChart();
      this.initExistingUserRetentionChart();
      this.initNewUserRetentionChart();
    });
  },

  methods: {
    ...mapWaitingActions('admin/stats', {
      loadUserEngagement: 'loading data',
      loadActiveUserRetention: 'loading data',
      loadExistingUserRetention: 'loading data',
      loadNewUserRetention: 'loading data',
    }),

    initUserEngagementChart() {
      const labels = this.userEngagement.map(t => this.$dayjs(t.date).format('DD.MM.'));
      const dau = this.userEngagement.map(t => t.dau);
      const wau = this.userEngagement.map(t => t.wau);
      const mau = this.userEngagement.map(t => t.mau);

      new Chart(this.$refs.userEngagementChart, { // eslint-disable-line
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              ...this.retention1dOptions,
              label: 'DAU',
              data: dau,
            },
            {
              ...this.retention7dOptions,
              label: 'WAU',
              data: wau,
            },
            {
              ...this.retention30dOptions,
              label: 'MAU',
              data: mau,
            },
          ],
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
              },
            }],
          },
        },
      });
    },

    initStickinessChart() {
      const labels = this.userEngagement.map(t => this.$dayjs(t.date).format('DD.MM.'));
      const stickiness = this.userEngagement.map(t => t.stickiness * 100);

      new Chart(this.$refs.stickinessChart, { // eslint-disable-line
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              ...this.retention7dOptions,
              label: 'Stickiness (DAU/MAU)',
              backgroundColor: [
                'rgba(153, 102, 255, 0.2)',
              ],
              borderColor: [
                'rgba(153, 102, 255, 1)',
              ],
              data: stickiness,
            },
          ],
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                min: 0,
                max: 100,
                beginAtZero: true,
                callback: v => `${v} %`,
              },
            }],
          },
        },
      });
    },

    initActiveUserRetentionChart() {
      const labels = this.activeUserRetention.map(t => this.$dayjs(t.date).format('DD.MM.'));
      const retention1d = this.activeUserRetention.map(t => t.retention1d * 100);
      const retention7d = this.activeUserRetention.map(t => t.retention7d * 100);
      const retention30d = this.activeUserRetention.map(t => t.retention30d * 100);

      new Chart(this.$refs.activeUserRetentionChart, { // eslint-disable-line
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              ...this.retention1dOptions,
              data: retention1d,
            },
            {
              ...this.retention7dOptions,
              data: retention7d,
            },
            {
              ...this.retention30dOptions,
              data: retention30d,
            },
          ],
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                callback: v => `${v} %`,
              },
            }],
          },
        },
      });
    },

    initExistingUserRetentionChart() {
      const labels = this.existingUserRetention.map(t => this.$dayjs(t.date).format('DD.MM.'));
      const retention1d = this.existingUserRetention.map(t => t.retention1d * 100);
      const retention7d = this.existingUserRetention.map(t => t.retention7d * 100);
      const retention30d = this.existingUserRetention.map(t => t.retention30d * 100);

      new Chart(this.$refs.existingUserRetentionChart, { // eslint-disable-line
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              ...this.retention1dOptions,
              data: retention1d,
            },
            {
              ...this.retention7dOptions,
              data: retention7d,
            },
            {
              ...this.retention30dOptions,
              data: retention30d,
            },
          ],
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                callback: v => `${v} %`,
              },
            }],
          },
        },
      });
    },

    initNewUserRetentionChart() {
      const labels = this.newUserRetention.map(t => this.$dayjs(t.date).format('DD.MM.'));
      const retention1d = this.newUserRetention.map(t => t.retention1d * 100);
      const retention7d = this.newUserRetention.map(t => t.retention7d * 100);
      const retention30d = this.newUserRetention.map(t => t.retention30d * 100);

      new Chart(this.$refs.newUserRetentionChart, { // eslint-disable-line
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              ...this.retention1dOptions,
              data: retention1d,
            },
            {
              ...this.retention7dOptions,
              data: retention7d,
            },
            {
              ...this.retention30dOptions,
              data: retention30d,
            },
          ],
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                callback: v => `${v} %`,
              },
            }],
          },
        },
      });
    },
  },
};
</script>
